import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  LoadingState,
  type Column,
} from "@agentnative/ui";
import { apiErrorMessage } from "../lib/api/client";
import { getOrganization, listMembers, memberRoleLabel } from "../lib/api/org";
import {
  LICENSE_STATUS_LABELS,
  LICENSE_TYPE_LABELS,
  isDesignPartner,
  licenseBadgeVariant,
  listLicensesForOrg,
} from "../lib/api/license";
import type { License, OrganizationMember } from "../lib/api/types";

export function AdminOrganizationDetailRoute() {
  const { orgId } = useParams({ from: "/admin/organizations/$orgId" });

  const orgQuery = useQuery({
    queryKey: ["admin-org-lookup", orgId],
    queryFn: () => getOrganization(orgId),
  });

  const membersQuery = useQuery({
    queryKey: ["admin-org-members", orgId],
    queryFn: () => listMembers(orgId, { limit: 100, offset: 0 }),
  });

  const licensesQuery = useQuery({
    queryKey: ["admin-org-licenses", orgId],
    queryFn: () => listLicensesForOrg(orgId, { limit: 100, offset: 0 }),
  });

  if (orgQuery.isLoading) {
    return <LoadingState message="Loading organization..." />;
  }

  if (orgQuery.isError) {
    return (
      <Alert variant="danger" title="Failed to load organization">
        {apiErrorMessage(orgQuery.error)}
      </Alert>
    );
  }

  const org = orgQuery.data!.organization;

  const memberColumns: Column<OrganizationMember>[] = [
    { key: "userId", label: "User ID", render: (m) => <span className="font-mono text-sm">{m.userId}</span> },
    { key: "role", label: "Role", render: (m) => memberRoleLabel(m.role) },
    {
      key: "joinedAt",
      label: "Joined",
      render: (m) => (m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : "-"),
    },
  ];

  const licenseColumns: Column<License>[] = [
    { key: "appId", label: "App ID", render: (l) => <span className="font-mono text-sm">{l.appId}</span> },
    { key: "tier", label: "Tier" },
    {
      key: "status",
      label: "Status",
      render: (l) => <Badge variant={licenseBadgeVariant(l.status)}>{LICENSE_STATUS_LABELS[l.status]}</Badge>,
    },
    {
      key: "licenseType",
      label: "Type",
      render: (l) => (
        <span>
          {LICENSE_TYPE_LABELS[l.licenseType]}
          {isDesignPartner(l) && l.designPartnerDiscountPercent === 0 && (
            <Badge variant="warning" size="sm" className="ml-2">
              trial
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (l) =>
        isDesignPartner(l) && l.designPartnerDiscountPercent === 0 ? (
          <Link to="/admin/licenses/$licenseId/convert" params={{ licenseId: l.id }}>
            <Button size="sm">Convert</Button>
          </Link>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{org.name}</h1>
          <p className="font-mono text-xs text-[var(--color-neutral-500)]">{org.id}</p>
        </div>
        <Link to="/admin/licenses/issue" search={{ organizationId: org.id }}>
          <Button>Issue License</Button>
        </Link>
      </div>

      <Card padding="lg">
        <CardBody>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Slug</dt>
              <dd className="font-mono">{org.slug}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Owner</dt>
              <dd className="font-mono text-sm">{org.ownerId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-[var(--color-neutral-500)]">Status</dt>
              <dd>
                <Badge variant={org.enabled ? "success" : "danger"}>
                  {org.enabled ? "Enabled" : "Disabled"}
                </Badge>
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      <div>
        <h2 className="mb-3 text-lg font-medium">Members</h2>
        {membersQuery.isLoading ? (
          <LoadingState size="sm" />
        ) : membersQuery.isError ? (
          <Alert variant="danger">{apiErrorMessage(membersQuery.error)}</Alert>
        ) : (
          <Card padding="none">
            <DataTable<OrganizationMember>
              data={membersQuery.data?.members ?? []}
              columns={memberColumns}
              emptyMessage="No members."
            />
          </Card>
        )}
      </div>

      <div>
        <h2 className="mb-3 text-lg font-medium">Licenses</h2>
        {licensesQuery.isLoading ? (
          <LoadingState size="sm" />
        ) : licensesQuery.isError ? (
          <Alert variant="danger">{apiErrorMessage(licensesQuery.error)}</Alert>
        ) : (
          <Card padding="none">
            <DataTable<License>
              data={licensesQuery.data?.licenses ?? []}
              columns={licenseColumns}
              emptyMessage="No licenses issued to this org yet."
            />
          </Card>
        )}
      </div>
    </div>
  );
}
